import { useEffect, useContext } from 'react';
import {} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButtonFab,
    SttChipFacilityRow,
    SttChipRestrictionType,
    SttTable,
    SttTopControls,
    SttPage,
} from '../../../sporttia/all';
import { useToggle, useTuples, useCrud } from '../../../lib/hooks';
import { getPath } from '../../Pages';
import { PERMISSIONS } from '../../../lib/permissions';

type Restriction = {
    id: string;
    name: string;
    type: string;
    dateend?: string;
    trash?: number;
    numBookings?: string;
    interval?: string;
    facilities?: unknown[];
};

export default function Restrictions(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [restrictions, setRestrictions, , Get] = useCrud<any>();
    const [filters, setFilter] = useTuples({
        name: '',
    });
    const [trash, toggleTrash] = useToggle(() => {
        loadRestrictions();
    });
    /**
     * Load restrictions
     */
    function loadRestrictions(params = { rows: 20 }) {
        Get<any>(`/scs/${cxt?.sc?.id}/bookings/restrictions`, {
            ...params,
            ...filters,
            trash,
        }).then(setRestrictions);
    }

    /**
     * Init
     */
    useEffect(() => {
        cxt?.setHeader({
            title: t('Restrictions'),
        });
    }, []);

    /**
     * Go to create a new one
     */
    function goCreateNew() {
        navigate(getPath('restriction', { id: 'create' }));
    }

    /**
     * Build num bookings column
     */
    const buildNumBookingsColumn = (restriction: Restriction): string => {
        let result = '';

        if (
            ['NUM_BOOKINGS', 'NUM_ACTIVE_BOOKINGS'].includes(restriction.type)
        ) {
            result = restriction.numBookings || '';
        }
        if (restriction.type === 'NUM_BOOKINGS') {
            result += ` / ${t(restriction.interval || '')}`;
        }

        return result;
    };

    // Render
    return (
        <SttPage title={t('Restrictions')}>
            <SttTopControls
                p={2}
                fields={
                    [
                        {
                            caption: t('Name'),
                            type: 'search',
                            name: 'name',
                            value: filters.name,
                            onEnterKey: loadRestrictions,
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                        },
                    ] as any
                }
                onToggleTrash={toggleTrash}
                onChange={setFilter}
                onFilter={loadRestrictions}
            />
            <SttTable
                onFetch={loadRestrictions}
                data={restrictions}
                columns={[
                    {
                        title: t('Name'),
                        align: 'left',
                        type: 'link',
                        value: (row: Restriction) => row.name || t('Unknown'),
                        to: (row: Restriction) =>
                            getPath('restriction', { id: row.id }),
                        params: (row: Restriction) => ({
                            trash: +(row.trash || 0),
                        }),
                    },
                    {
                        title: t('Type'),
                        type: 'text',
                        value: (row: Restriction) => (
                            <SttChipRestrictionType type={row.type} />
                        ),
                    },
                    {
                        title: t('Validity'),
                        type: 'dmy',
                        field: 'dateend',
                    },
                    {
                        title: t('Bookings'),
                        type: 'text',
                        field: 'numBookingsPerInterval',
                        value: (row: Restriction) =>
                            buildNumBookingsColumn(row),
                        width: 150,
                    },
                    {
                        title: t('Facilities'),
                        value: (row: Restriction) => (
                            <SttChipFacilityRow
                                maxWidth={220}
                                facilities={row.facilities as any}
                            />
                        ),
                    },
                ]}
            />
            <SttButtonFab
                onClick={goCreateNew}
                permission={PERMISSIONS.RESTRICTION.CREATE}
            />
        </SttPage>
    );
}
